"use client";

import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation"; // Import usePathname
import logo from "@/assets/footer/Autoeinfachlos_logo_weiss.webp";
import Image from "next/image";
import "./Navbar.css";
import Button from "@/common/Button";
import Link from "next/link";
import { useAuth } from "@/context/AuthProvider";
import { scrollToSection } from "@/app/helpers/scrollToSection";
import hamburgerIcon from "@/assets/svgs/hamburger.svg";
import overviewIcon from "@/assets/svgs/overview.svg";
import chevronIcon from "@/assets/svgs/chevron.svg";
import questionMarkIcon from "@/assets/svgs/questionMark.svg";
import logoutIcon from "@/assets/svgs/logoutIcon.svg";
import saleIcon from "@/assets/svgs/sale.svg";
import notificationIcon from "@/assets/svgs/notification.svg";
import praeferenzenIcon from "@/assets/svgs/praeferenzen.svg";
import saveSearchIcon from "@/assets/svgs/saveSearch.svg";
import useTPStatus from "@/app/hooks/useTPStatus";
import {
  canDealerSell,
  isDealerB2BEligible,
} from "@/app/helpers/dealerAccess";
import { getUserInitials } from "@/app/helpers/getUserInitials";
import { useDealerVehicleMarket } from "@/context/DealerVehicleMarketProvider";

function Navbar({
  hasVehicleAction,
  hasOpenCounterOffer,
  hasOverviewTask,
  isNavbarDark,
  isNavbarSticky,
  isReroutingToLanding,
  setIsSidebarOpen,
}) {
  const { userData, logout, authInitialized } = useAuth();
  const { enabled: dealerVehicleMarketEnabled } = useDealerVehicleMarket();
  const [scrolled, setScrolled] = useState(false);

  // Get current pathname
  const pathname = usePathname();
  const usesVehicleListTheme = [
    "/profil/gebote/im-prozess",
    "/profil/gebote/nicht-gewonnen",
    "/profil/gebote/abgeschlossen",
    "/profil/gebote/storniert",
  ].includes(pathname) || pathname.startsWith("/verkaufen/gegenangebote");
  const usesInvoiceDashboardTheme = [
    "/profil/gebote/offene-rechnungen",
    "/profil/gebote/bezahlte-rechnungen",
  ].includes(pathname);
  const dashboardThemeClass =
    pathname === "/profil/gebote/aktiv"
      ? "Navbar--active-bids"
      : usesInvoiceDashboardTheme
        ? "Navbar--invoice-dashboard"
      : usesVehicleListTheme
        ? "Navbar--in-process"
        : "";
  const isStaticLegalPage = [
    "/allgemeine-geschaeftsbedingungen",
    "/datenschutzerklaerung",
  ].includes(pathname);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50 && isNavbarSticky) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isNavbarSticky]);

  const hasAccess = userData?.companyInformation?.accessGranted;
  const hasB2BAccess = isDealerB2BEligible(userData);
  const sellerAccess =
    dealerVehicleMarketEnabled && canDealerSell(userData);
  const { activeProgram } = useTPStatus(userData);
  const showTreueprogrammNav = hasB2BAccess && !!activeProgram?.id;

  if (isStaticLegalPage) {
    return (
      <nav
        className={`Navbar ${isNavbarDark && "dark"} ${
          isNavbarSticky && "sticky"
        } ${scrolled ? "scrolled" : ""} ${dashboardThemeClass}`}
      >
        <div className="Navbar-wrapper">
          <div className="Navbar-left">
            <Image src={logo} className="Navbar-logo" alt="logo" />
          </div>
        </div>
      </nav>
    );
  }

  // For dropdowns, mark parent active if any child matches the current path
  const openDealsActive = [
    "/profil/gebote/aktiv",
    "/profil/gebote/im-prozess",
    "/profil/gebote/nicht-gewonnen",
  ].includes(pathname);
  const kaufeActive = [
    "/profil/gebote/abgeschlossen",
    "/profil/gebote/storniert",
    "/profil/gebote/offene-rechnungen",
    "/profil/gebote/bezahlte-rechnungen",
  ].includes(pathname);
  const verkaufeActive = pathname.startsWith("/verkaufen");

  if (userData && !hasB2BAccess) {
    return (
      <nav
        className={`Navbar ${isNavbarDark && "dark"} ${
          isNavbarSticky && "sticky"
        } ${scrolled ? "scrolled" : ""} ${dashboardThemeClass}`}
      >
        <div className="Navbar-wrapper">
          <div className="Navbar-left">
            <Link
              href="/"
            >
              <Image src={logo} className="Navbar-logo" alt="logo" />
            </Link>
          </div>

          <div className="Navbar-right Navbar-mobile-only">
            <button
              type="button"
              className="Navbar-mobile-hamburger-wrapper"
              aria-label="Navigation öffnen"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Image
                src={hamburgerIcon}
                alt=""
                className="Navbar-hamburger-icon"
              />
            </button>
          </div>

          {userData ? (
            <div className="Navbar-right Navbar-desktop-only">
              <ul className="Navbar-items-wrapper">
                <li
                  className={`Navbar-item ${
                    pathname === "/profil/unternehmensinformation"
                      ? "active"
                      : ""
                  }`}
                >
                  <Link href="/profil/unternehmensinformation">
                    Unternehmensinformation
                  </Link>
                </li>
              </ul>
              <li
                className="Navbar-item dropdown"
                style={{ position: "relative" }}
              >
                <div className="dropdown-title-wrapper konto-dropdown">
                  <div className="links mobile-nav-account-button">
                    <p>{getUserInitials(userData)}</p>
                  </div>
                </div>
                <ul className="dropdown-menu">
                  <Link
                    href={
                      hasAccess
                        ? "/profil/unternehmensinformation"
                        : "/verifikation"
                    }
                    className="dropdown-links"
                  >
                    <li className="transition dropdown-user-wrapper">
                      <div className="dropdown-left">
                        <span className="dropdown-user-text-main">
                          {userData.vorname} {userData.nachname}
                        </span>
                        <span className="dropdown-user-text-secondary">
                          {userData.firmenname}
                        </span>
                      </div>

                      {hasAccess && (
                        <div className="dropdown-right">
                          <Image
                            src={chevronIcon}
                            alt="chevron"
                            className="dropdown-right-chevron"
                          />
                        </div>
                      )}
                    </li>
                  </Link>

                  <div className="dropdown-divider-wrapper">
                    <div className="dropdown-divider"></div>
                  </div>

                  <div className="dropdown-links" onClick={() => logout()}>
                    <li className="transition konto-item-wrapper">
                      <div className="dropdown-item-wrapper">
                        <Image
                          src={logoutIcon}
                          alt="logout"
                          className="dropdown-item-icon"
                        />
                        <span>Ausloggen</span>
                      </div>
                    </li>
                  </div>
                </ul>
              </li>
            </div>
          ) : authInitialized ? (
            <div className="Navbar-right Navbar-desktop-only">
              <ul className="Navbar-items-wrapper">
                <li
                  className="Navbar-item"
                  onClick={() => scrollToSection("how-it-works", "/")}
                >
                  So funktionierts
                </li>
              </ul>

              <Button
                paddingVertical="12px"
                paddingHorizontal="24px"
                isSecondary={true}
                link="/login"
              >
                Login
              </Button>
              <Button
                paddingVertical="12px"
                paddingHorizontal="24px"
                isSecondary={false}
                link="/registrieren"
              >
                Registrieren
              </Button>
            </div>
          ) : (
            <div
              className="Navbar-right Navbar-desktop-only Navbar-auth-pending"
              aria-hidden="true"
            />
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`Navbar ${isNavbarDark && "dark"} ${
        isNavbarSticky && "sticky"
      } ${scrolled ? "scrolled" : ""} ${dashboardThemeClass}`}
    >
      <div className="Navbar-wrapper">
        <div className="Navbar-left">
          <Link href={isReroutingToLanding || !hasB2BAccess ? "/" : "/marktplatz"}>
            <Image src={logo} className="Navbar-logo" alt="logo" />
          </Link>

          {hasB2BAccess && (
            <ul className="Navbar-items-wrapper">
              <li
                className={`Navbar-item ${pathname === "/faq" ? "active" : ""}`}
              >
                <Link href="/faq">Hilfezentrum</Link>
              </li>
            </ul>
          )}
        </div>

        <div className="Navbar-right Navbar-mobile-only">
          <button
            type="button"
            className="Navbar-mobile-hamburger-wrapper"
            aria-label="Navigation öffnen"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Image
              src={hamburgerIcon}
              alt=""
              className="Navbar-hamburger-icon"
            />
          </button>
        </div>

        {userData ? (
          <div className="Navbar-right Navbar-desktop-only">
            <ul className="Navbar-items-wrapper">
              {hasB2BAccess && (
                <>
                  {showTreueprogrammNav && (
                    <li
                      className={`Navbar-item ${
                        pathname === "/treueprogramm" ? "active" : ""
                      }`}
                    >
                      <Link href="/treueprogramm">Treueprogramm</Link>
                    </li>
                  )}
                  <li
                    className={`Navbar-item ${
                      pathname === "/marktplatz" ? "active" : ""
                    }`}
                  >
                    <Link href="/marktplatz">Marktplatz</Link>
                  </li>
                  <li
                    className={`Navbar-item dropdown ${
                      openDealsActive ? "active" : ""
                    }`}
                    style={{ position: "relative" }}
                  >
                    <div className="dropdown-title-wrapper">
                      <span>Offene deals</span>
                      <Image
                        src={chevronIcon}
                        alt="chevron"
                        className="Navbar-chevron"
                      />
                    </div>
                    <ul className="dropdown-menu">
                      <Link
                        href="/profil/gebote/aktiv"
                        className="dropdown-links"
                      >
                        <li
                          className={`transition konto-item-wrapper ${
                            pathname === "/profil/gebote/aktiv" ? "active" : ""
                          }`}
                        >
                          Aktiv
                        </li>
                      </Link>
                      <Link
                        href="/profil/gebote/im-prozess"
                        className="dropdown-links"
                      >
                        <li
                          className={`transition konto-item-wrapper ${
                            pathname === "/profil/gebote/im-prozess"
                              ? "active"
                              : ""
                          }`}
                        >
                          Im Prozess
                        </li>
                      </Link>
                      <Link
                        href="/profil/gebote/nicht-gewonnen"
                        className="dropdown-links"
                      >
                        <li
                          className={`transition konto-item-wrapper ${
                            pathname === "/profil/gebote/nicht-gewonnen"
                              ? "active"
                              : ""
                          }`}
                        >
                          Nicht gewonnen
                        </li>
                      </Link>
                    </ul>
                  </li>
                  <li
                    className={`Navbar-item dropdown ${
                      kaufeActive ? "active" : ""
                    }`}
                    style={{ position: "relative" }}
                  >
                    <div className="dropdown-wrapper">
                      <div className="dropdown-title-wrapper">
                        <span>Käufe</span>
                        <Image
                          src={chevronIcon}
                          alt="chevron"
                          className="Navbar-chevron"
                        />
                      </div>
                      <ul className="dropdown-menu">
                        <Link
                          href="/profil/gebote/abgeschlossen"
                          className="dropdown-links"
                        >
                          <li
                            className={`transition konto-item-wrapper ${
                              pathname === "/profil/gebote/abgeschlossen"
                                ? "active"
                                : ""
                            }`}
                          >
                            Abgeschlossen
                          </li>
                        </Link>
                        <Link
                          href="/profil/gebote/storniert"
                          className="dropdown-links"
                        >
                          <li
                            className={`transition konto-item-wrapper ${
                              pathname === "/profil/gebote/storniert"
                                ? "active"
                                : ""
                            }`}
                          >
                            Storniert
                          </li>
                        </Link>
                        <Link
                          href="/profil/gebote/offene-rechnungen"
                          className="dropdown-links"
                        >
                          <li
                            className={`transition konto-item-wrapper ${
                              pathname === "/profil/gebote/offene-rechnungen"
                                ? "active"
                                : ""
                            }`}
                          >
                            Offene Rechnungen
                          </li>
                        </Link>
                        <Link
                          href="/profil/gebote/offene-rechnungen"
                          className="dropdown-links"
                        >
                          <li
                            className={`transition konto-item-wrapper ${
                              pathname === "/profil/gebote/bezahlte-rechnungen"
                                ? "active"
                                : ""
                            }`}
                          >
                            Bezahlte Rechnungen
                          </li>
                        </Link>
                      </ul>
                    </div>
                  </li>
                  {sellerAccess && (
                    <li
                      className={`Navbar-item dropdown ${
                        verkaufeActive ? "active" : ""
                      }`}
                      style={{ position: "relative" }}
                    >
                      <div className="dropdown-wrapper">
                        <div className="dropdown-title-wrapper">
                          <span>Verkäufe</span>
                          <Image
                            src={chevronIcon}
                            alt="chevron"
                            className="Navbar-chevron"
                          />
                        </div>
                        <ul className="dropdown-menu">
                          <Link
                            href="/verkaufen/meine-fahrzeuge"
                            className="dropdown-links"
                          >
                            <li
                              className={`transition konto-item-wrapper Navbar-vehicleActionItem ${
                                pathname === "/verkaufen/meine-fahrzeuge"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span>Meine Fahrzeuge</span>
                              {hasVehicleAction && (
                                <span
                                  aria-label="Aktion für ein Fahrzeug erforderlich"
                                  className="Navbar-vehicleActionIndicator"
                                  role="status"
                                />
                              )}
                            </li>
                          </Link>
                          <Link href="/verkaufen" className="dropdown-links">
                            <li
                              className={`transition konto-item-wrapper ${
                                pathname === "/verkaufen" ? "active" : ""
                              }`}
                            >
                              Fahrzeug anlegen
                            </li>
                          </Link>
                          <Link
                            href="/verkaufen/gegenangebote"
                            className="dropdown-links"
                          >
                            <li
                              className={`transition konto-item-wrapper Navbar-vehicleActionItem ${
                                pathname.startsWith(
                                  "/verkaufen/gegenangebote",
                                )
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span>Gegengebote</span>
                              {hasOpenCounterOffer && (
                                <span
                                  aria-label="Offenes Gegengebot unter dem Händler-Mindestpreis"
                                  className="Navbar-vehicleActionIndicator"
                                  role="status"
                                />
                              )}
                            </li>
                          </Link>
                        </ul>
                      </div>
                    </li>
                  )}
                </>
              )}
            </ul>
            <li
              className="Navbar-item dropdown"
              style={{ position: "relative" }}
            >
              <div className="dropdown-title-wrapper konto-dropdown">
                <div className="links mobile-nav-account-button">
                  <p>{getUserInitials(userData)}</p>
                  {hasOverviewTask && (
                    <span
                      aria-label="Offene Aufgabe in der Übersicht"
                      className="Navbar-vehicleActionIndicator Navbar-vehicleActionIndicator--avatar"
                      role="status"
                    />
                  )}
                </div>
              </div>
              <ul className="dropdown-menu">
                <Link
                  href={hasB2BAccess ? "/profil/uebersicht" : "/verifikation"}
                  className="dropdown-links"
                >
                  <li className="transition dropdown-user-wrapper">
                    <div className="dropdown-left">
                      <span className="dropdown-user-text-main">
                        {userData.vorname} {userData.nachname}
                      </span>
                      <span className="dropdown-user-text-secondary">
                        {userData.firmenname}
                      </span>
                    </div>

                    {hasB2BAccess && (
                      <div className="dropdown-right">
                        <Image
                          src={chevronIcon}
                          alt="chevron"
                          className="dropdown-right-chevron"
                        />
                      </div>
                    )}
                  </li>
                </Link>
                {hasB2BAccess && (
                  <>
                    <Link
                      className="dropdown-links"
                      href="/profil/uebersicht"
                      aria-label={
                        hasOverviewTask
                          ? "Übersicht – offene Aufgabe"
                          : undefined
                      }
                    >
                      <li className="transition konto-item-wrapper Navbar-vehicleActionItem">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={overviewIcon}
                            alt="Übersicht"
                            className="dropdown-item-icon"
                          />
                          <span>Übersicht</span>
                        </div>
                        {hasOverviewTask && (
                          <span
                            aria-hidden="true"
                            className="Navbar-vehicleActionIndicator"
                          />
                        )}
                      </li>
                    </Link>
                    <Link className="dropdown-links" href="/faq">
                      <li className="transition konto-item-wrapper">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={questionMarkIcon}
                            alt="Hilfezentrum"
                            className="dropdown-item-icon"
                          />
                          <span>Hilfe</span>
                        </div>
                      </li>
                    </Link>
                    <Link className="dropdown-links" href="/profil/gebuehren">
                      <li className="transition konto-item-wrapper">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={saleIcon}
                            alt="Gebühren"
                            className="dropdown-item-icon"
                          />
                          <span>Gebühren</span>
                        </div>
                      </li>
                    </Link>

                    <Link
                      className="dropdown-links"
                      href="/profil/benachrichtigungscenter"
                    >
                      <li className="transition konto-item-wrapper">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={notificationIcon}
                            alt="Notification Icon"
                            className="dropdown-item-icon"
                          />
                          <span>Benachrichtigungen</span>
                        </div>
                      </li>
                    </Link>

                    <Link
                      className="dropdown-links"
                      href="/profil/gespeicherte-suchen"
                    >
                      <li className="transition konto-item-wrapper">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={saveSearchIcon}
                            alt="Gespeicherte suchen Icon"
                            style={{
                              width: "18px",
                              height: "18px",
                            }}
                          />
                          <span>Gespeicherte Suchen</span>
                        </div>
                      </li>
                    </Link>

                    <Link
                      className="dropdown-links"
                      href="/profil/praeferenzen"
                    >
                      <li className="transition konto-item-wrapper">
                        <div className="dropdown-item-wrapper">
                          <Image
                            src={praeferenzenIcon}
                            alt="Präferenzen Icon"
                            className="dropdown-item-icon"
                          />
                          <span>Präferenzen</span>
                        </div>
                      </li>
                    </Link>
                  </>
                )}

                <div className="dropdown-divider-wrapper">
                  <div className="dropdown-divider"></div>
                </div>

                <div className="dropdown-links" onClick={() => logout()}>
                  <li className="transition konto-item-wrapper">
                    <div className="dropdown-item-wrapper">
                      <Image
                        src={logoutIcon}
                        alt="logout"
                        className="dropdown-item-icon"
                      />
                      <span>Ausloggen</span>
                    </div>
                  </li>
                </div>
              </ul>
            </li>
          </div>
        ) : authInitialized ? (
          <div className="Navbar-right Navbar-desktop-only">
            <ul className="Navbar-items-wrapper">
              <li
                className="Navbar-item"
                onClick={() => scrollToSection("how-it-works", "/")}
              >
                So funktionierts
              </li>
            </ul>

            <Button
              paddingVertical="12px"
              paddingHorizontal="24px"
              isSecondary={true}
              link="/login"
            >
              Login
            </Button>
            <Button
              paddingVertical="12px"
              paddingHorizontal="24px"
              isSecondary={false}
              link="/registrieren"
            >
              Registrieren
            </Button>
          </div>
        ) : (
          <div
            className="Navbar-right Navbar-desktop-only Navbar-auth-pending"
            aria-hidden="true"
          />
        )}
      </div>
    </nav>
  );
}

export default Navbar;
